import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { awsConfig } from "../aws-config";
import { getAuthHeader, getWsToken } from "../api";

interface Message {
  conversationId: string;
  sentAt: string;
  message: string;
  senderId: string;
  recipientId: string;
}

const ADMIN_ID = "admin";

export default function SupportChatPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const issue = searchParams.get("issue") || "";
  const bookingId = searchParams.get("bookingId") || "";
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState("");
  const [wsToken, setWsToken] = useState("");
  const wsRef = useRef<WebSocket | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const wsAttemptsRef = useRef(0);
  const [wsRetry, setWsRetry] = useState(0);

  useEffect(() => {
    getWsToken().then(token => {
      if (token && token.split(".").length === 3) {
        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          setUserId(payload.sub || "");
          setWsToken(token);
        } catch {}
      }
    }).catch(() => {});
  }, []);

  const conversationId = userId ? `support_${userId}` : "";

  useEffect(() => {
    if (!userId || !wsToken) return;

    async function loadMessages() {
      try {
        const headers = await getAuthHeader();
        const response = await axios.get(
          `${awsConfig.apiUrl}/messages?conversationId=${conversationId}`,
          { headers }
        );
        setMessages(response.data.messages || []);
      } catch (err) {
        console.error("Failed to load messages:", err);
      } finally {
        setLoading(false);
      }
    }

    loadMessages();

    let disposed = false;
    const ws = new WebSocket(`${awsConfig.wsUrl}?token=${encodeURIComponent(wsToken)}`);
    wsRef.current = ws;

    ws.onopen = () => { wsAttemptsRef.current = 0; };
    ws.onclose = () => {
      // Reconnect with a freshly refreshed token — ID tokens expire after
      // 1h, so a long-lived tab needs a new one to reopen the socket.
      if (!disposed && wsAttemptsRef.current < 5) {
        wsAttemptsRef.current += 1;
        setTimeout(() => {
          if (disposed) return;
          getWsToken()
            .then(t => { if (t) setWsToken(t); setWsRetry(r => r + 1); })
            .catch(() => {});
        }, 3000);
      }
    };
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.conversationId === conversationId) {
        setMessages(prev => [...prev, data]);
      }
    };

    return () => { disposed = true; ws.close(); };
  }, [userId, wsToken, conversationId, wsRetry]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function sendMessage() {
    if (!newMessage.trim() || !wsRef.current) return;
    const ws = wsRef.current;
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        action: "sendMessage",
        conversationId,
        message: newMessage,
        senderId: userId,
        recipientId: ADMIN_ID
      }));
      setMessages(prev => [...prev, {
        conversationId,
        sentAt: new Date().toISOString(),
        message: newMessage,
        senderId: userId,
        recipientId: ADMIN_ID
      }]);
      setNewMessage("");
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="bg-white px-5 pt-4 pb-4 shadow-sm flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-gray-400 font-bold text-lg">←</button>
        <div className="w-10 h-10 rounded-xl bg-mouna-blue flex items-center justify-center text-lg">🛡️</div>
        <div>
          <div className="font-bold text-navy text-sm">Mouna Support</div>
          <div className="text-xs text-teal">We typically reply within 1 hour</div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 pb-24">
        {loading && (
          <div className="flex justify-center py-8">
            <div className="w-6 h-6 border-4 border-mouna-blue border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {!loading && messages.length === 0 && (
          <div className="text-center py-12">
            <div className="text-4xl mb-3">👋</div>
            <div className="font-bold text-navy">How can we help?</div>
            <div className="text-xs text-gray-400 mt-1">Send us a message and we'll get back to you shortly</div>
            <div className="mt-6 space-y-2">
              {(issue === "reschedule" ? ["I need to reschedule my booking", "Can I change the date?", "How does rescheduling work?"] :
                issue === "cancel" ? ["I want to cancel my booking", "What is the cancellation policy?", "Will I get a refund?"] :
                issue === "modify" ? ["I need to update my address", "The booking details are wrong", "Can I change the service?"] :
                issue === "callback" ? ["I need a callback as soon as possible", "Please call me today", "I have an urgent issue"] :
                ["How does lead pricing work?", "I have an issue with a booking", "How do I get verified?"]).map(q => (
                <button key={q} onClick={() => setNewMessage(q)}
                  className="block w-full text-left bg-white rounded-xl px-4 py-3 text-sm text-navy shadow-sm border border-gray-100">
                  {q} →
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => {
          const isMe = msg.senderId === userId;
          return (
            <div key={i} className={`flex mb-3 ${isMe ? "justify-end" : "justify-start"}`}>
              {!isMe && (
                <div className="w-8 h-8 rounded-full bg-mouna-blue flex items-center justify-center text-white text-xs font-bold mr-2 flex-shrink-0">M</div>
              )}
              <div className={`max-w-xs px-4 py-2 rounded-2xl text-sm ${isMe ? "bg-mouna-blue text-white rounded-tr-sm" : "bg-white text-navy rounded-tl-sm shadow-sm"}`}>
                {msg.message}
                <div className={`text-xs mt-1 ${isMe ? "text-blue-200" : "text-gray-400"}`}>
                  {new Date(msg.sentAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 py-3">
        <div className="flex items-center gap-2">
          <input type="text" placeholder="Type a message..."
            value={newMessage} onChange={e => setNewMessage(e.target.value)}
            onKeyPress={e => e.key === "Enter" && sendMessage()}
            className="flex-1 border border-gray-200 rounded-full px-4 py-2 text-sm outline-none focus:border-mouna-blue"/>
          <button onClick={sendMessage}
            className="w-10 h-10 bg-mouna-blue rounded-full flex items-center justify-center text-white font-bold">
            →
          </button>
        </div>
      </div>
    </div>
  );
}
